import { useState } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../app/store";
import { useGetMentorsQuery, useUpdateMentorStatusMutation } from "../api/adminApi";
import { Link } from "react-router-dom";

export const MentorsTable = () => {
  const { data: mentors = [], isLoading, error } = useGetMentorsQuery();
  const [selectedMentorFamilies, setSelectedMentorFamilies] = useState<{ id: string; name: string }[] | null>(null);
  const [localStatus, setLocalStatus] = useState<{ [id: string]: boolean }>(() =>
    Object.fromEntries(mentors.map((m) => [m.id, m.active]))
  );
  const [updateMentorStatus] = useUpdateMentorStatusMutation();
  const search = useSelector((state: RootState) => state.mentorsFilters.search.trim().toLowerCase());

  const handleStatusChange = async (mentorId: string, newValue: string) => {
    const newStatus = newValue === "true";
    setLocalStatus((prev) => ({
      ...prev,
      [mentorId]: newStatus,
    }));
    await updateMentorStatus({ id: mentorId, active: newStatus });
  };

  const handleButtonClick = (mentorId: string) => {
    const selected = mentors.find((m) => m.id === mentorId);
    if (selected) {
      const familyDetails = selected.families.map((f) => ({
        id: f.id,
        name: f.familyName,
      }));
      setSelectedMentorFamilies(familyDetails);
    }
  };

  const filteredMentors = mentors.filter((mentor) =>
    mentor.name.toLowerCase().includes(search) ||
    mentor.families.some((f) => f.familyName.toLowerCase().includes(search))
  );

  if (isLoading) return <p>טוען נתונים...</p>;
  if (error) return <p>שגיאה בטעינת המידע</p>;

  return (
    <div className="w-full">
      <div className="w-full rounded-xl overflow-x-auto">
        <table className="w-full table-fixed text-sm text-right border-separate border-spacing-y-2">
          <thead className="bg-gray-100 text-gray-700">
            <tr>
              <th className="py-3 px-4 text-right">שם מנטור</th>
              <th className="py-3 text-center">מס' משפחות</th>
              <th className="py-3 text-center">סטטוס</th>
              <th className="py-3 text-center">משפחות בטיפול</th>
            </tr>
          </thead>
          <tbody>
            {filteredMentors.map((mentor) => (
              <tr
                key={mentor.id}
                className="hover:bg-gray-50 transition bg-white rounded-xl shadow-sm"
              >
                <td className="py-3 px-4 text-right">{mentor.name}</td>
                <td className="py-3 text-center">{mentor.familyCount}</td>
                <td className="py-3 text-center">
                  <select
                    value={localStatus[mentor.id]?.toString() ?? mentor.active.toString()}
                    onChange={(e) => handleStatusChange(mentor.id, e.target.value)}
                    className="px-2 py-1 rounded border border-gray-300 text-sm focus:outline-none focus:ring focus:ring-emerald-200"
                  >
                    <option value="true">פעיל</option>
                    <option value="false">לא פעיל</option>
                  </select>
                </td>
                <td className="py-3 text-center">
                  <button
                    onClick={() => handleButtonClick(mentor.id)}
                    className="bg-primary text-white px-3 py-1 rounded hover:bg-primary-dark transition"
                  >
                    לרשימת המשפחות
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {selectedMentorFamilies && (
        <div className="mt-6 bg-gray-50 p-4 rounded shadow text-right">
          <h2 className="text-lg font-semibold mb-2">משפחות בטיפול:</h2>
          <ul className="list-disc pr-6">
            {selectedMentorFamilies.map((family) => (
              <li key={family.id}>
                <Link to={`/families/${family.id}`} className="text-blue-600 hover:underline">
                  {family.name}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};
